import React from 'react';
import styled from 'styled-components';
import Section from '../components/common/Section';
import CallToAction from '../components/sections/CallToAction';
import { motion } from 'framer-motion';
import { FaUsers, FaLightbulb, FaHandshake } from 'react-icons/fa';

const AboutContainer = styled.div``;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  background-color: var(--primary-color);
  color: white;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ValueSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ValueItem = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(0, 85, 164, 0.1);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  margin: 0 auto 1.5rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ValueText = styled.p`
  color: var(--text-light);
  line-height: 1.6;
`;

const VisionText = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: var(--text-light);
`;

const About = () => {
  const coreValues = [
    {
      icon: <FaLightbulb />,
      title: 'US Energy Trading Compliance Innovation',
      text: 'We continuously innovate US energy trading compliance solutions, staying ahead of CFTC regulations, position limits requirements, and gas market compliance trends.'
    },
    {
      icon: <FaHandshake />,
      title: 'Energy Trading Compliance Partnership',
      text: 'We build long-term partnerships with US energy trading firms, working as extensions of their compliance teams to solve complex position limits monitoring and regulatory challenges.'
    },
    {
      icon: <FaUsers />,
      title: 'Energy Trading Compliance Excellence',
      text: 'We are committed to delivering exceptional US energy trading compliance solutions, from position limits monitoring software to CFTC regulatory reporting and gas market compliance support.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <AboutContainer>
      <HeroSection>
        <div className="container">
          <HeroTitle>About Talgo - US Energy Trading Compliance Experts</HeroTitle>
          <HeroSubtitle>
            Leading provider of US gas and energy trading compliance solutions with specialized expertise in position limits monitoring, CFTC compliance, and regulatory reporting for commodity trading firms.
          </HeroSubtitle>
        </div>
      </HeroSection>

      <Section
        title="US Energy Trading Compliance Specialists"
        subtitle="Founded in 2015, Talgo specializes in US energy trading compliance with deep expertise in position limits monitoring, CFTC regulations, and gas market compliance solutions."
        titleProps={{ centered: true, withLine: true }}
        subtitleProps={{ centered: true, muted: true }}
      >
        <VisionText>
          Our team of US energy trading compliance experts recognized the complex challenges faced by commodity trading firms navigating CFTC regulations, position limits monitoring, and gas market compliance requirements. We built Talgo to provide specialized US energy trading compliance solutions, focusing on position limits monitoring, regulatory reporting, and CFTC compliance for gas and energy markets. Today, we serve leading US energy trading firms, helping them maintain compliance with position limits, CFTC regulations, and gas market requirements while optimizing their trading operations.
        </VisionText>
      </Section>

      <Section
        title="Our Core Values"
        offWhite
        titleProps={{ centered: true, withLine: true, spaced: true }}
      >
        <ValueSection
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {coreValues.map((value, index) => (
            <ValueItem key={index} variants={itemVariants}>
              <ValueIcon>{value.icon}</ValueIcon>
              <ValueTitle>{value.title}</ValueTitle>
              <ValueText>{value.text}</ValueText>
            </ValueItem>
          ))}
        </ValueSection>
      </Section>

      <CallToAction />
    </AboutContainer>
  );
};

export default About; 